import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Button,
  Popconfirm,
  notification,
  Space,
  Breadcrumb,
  Spin,
  Dropdown,
  Menu,
  Typography,
  Select,
  Input,
  Modal,
} from "antd";
import {
  FolderFilled,
  FileOutlined,
  FilePdfOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  HomeOutlined,
  DownloadOutlined,
  EyeOutlined,
  SearchOutlined,
  ClearOutlined,
  MailOutlined,
  ShareAltOutlined,
  MergeCellsOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Highlighter from "react-highlight-words";
import {
  fetchDocuments,
  createDocument,
  deleteDocuments,
  renameDocument,
  uploadFolderTreeToDocument,
  createRootDocument,
  downloadDocument,
  updateDocument,
  moveDocument,
  notifyProjectUsersAboutDocument,
  fetchDocumentFolderShare,
  shareDocumentFolder,
  unshareDocumentFolder,
  fetchDocumentMergeSummary,
  mergeDocumentFolderPDFs,
} from "../services/documentsAPI";
import { useAuth } from "../hooks/useAuth";
import CreateDocumentModal from "../components/Documents/CreateDocumentModal";
import UploadFileModal from "../components/Documents/UploadFileModal";
import UpdateDocumentModal from "../components/Documents/UpdateDocumentModal";
import DocumentPreview from "../components/Documents/DocumentPreview";
import NotifyUsersModal from "../components/NotifyUsersModal";
import ShareFolderModal from "../components/ShareFolderModal";
import { downloadBlob } from "../utils/downloadUtils";
import { formatDateLocal } from "../utils/dateUtils";
import {
  getPreferredPageSize,
  savePreferredPageSize,
} from "../utils/paginationUtils";

const Documents = () => {
  const { selectedProject } = useAuth();
  const projectId = selectedProject?.project_id;
  const userRole = selectedProject?.role;
  const isOwnerOrManager =
    userRole === "owner" || userRole === "project_manager";
  const [documentId, setDocumentId] = useState(null);
  const [breadcrumbPath, setBreadcrumbPath] = useState([]); // Track nested folder path
  const [breadcrumbItems, setBreadcrumbItems] = useState([]);
  const [isCreateFolderModalVisible, setIsCreateFolderModalVisible] =
    useState(false);
  const [isUploadFileModalVisible, setIsUploadFileModalVisible] =
    useState(false);
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [downloadingDocumentId, setDownloadingDocumentId] = useState(null);
  const [isNotifyUsersModalVisible, setIsNotifyUsersModalVisible] =
    useState(false);
  const [documentToNotify, setDocumentToNotify] = useState(null);
  const [notifyingDocumentId, setNotifyingDocumentId] = useState(null);
  const [folderToShare, setFolderToShare] = useState(null);
  const [isMergingPDFs, setIsMergingPDFs] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: getPreferredPageSize("documents"),
    total: 0,
  });
  const [filters, setFilters] = useState({});
  const [searchTexts, setSearchTexts] = useState({});
  const [searchedColumns, setSearchedColumns] = useState([]);
  const searchInput = useRef(null);
  const queryClient = useQueryClient();
  const [draggingIds, setDraggingIds] = useState([]);
  const [dropTargetId, setDropTargetId] = useState(null);
  const suppressClickRef = useRef(false);

  // Fetch documents
  const { data: documentsData, isLoading: documentsLoading } = useQuery({
    queryKey: [
      "documents",
      projectId,
      documentId,
      pagination.current,
      pagination.pageSize,
      filters,
    ],
    queryFn: ({ signal }) =>
      fetchDocuments(
        projectId,
        documentId,
        pagination.current,
        pagination.pageSize,
        filters,
        { signal },
      ),
    enabled: !!projectId,
    keepPreviousData: true,
    onError: (error) => {
      const errorMessage =
        error.response?.data?.detail?.message ||
        error.response?.data?.detail ||
        error.message;
      notification.error({
        message: "Failed to load documents",
        description: errorMessage,
      });
    },
  });

  // Reset state when project changes
  useEffect(() => {
    if (projectId) {
      // Cancel any pending queries first to prevent stale requests
      queryClient.cancelQueries({
        queryKey: ["documents"],
        exact: false,
      });
      // Reset all state immediately
      setDocumentId(null);
      setBreadcrumbPath([]);
      setSelectedRowKeys([]);
      setPreviewDocument(null);
      setIsPreviewModalVisible(false);
      setPagination((prev) => ({
        ...prev,
        current: 1,
      }));
      setFilters({});
      setSearchTexts({});
      setSearchedColumns([]);
      // Remove all queries from previous project to clear cache
      queryClient.removeQueries({
        queryKey: ["documents"],
        exact: false,
      });
    }
  }, [projectId, queryClient]);

  // Initialize root folder on mount
  useEffect(() => {
    if (projectId && !documentId) {
      createRootDocument(projectId).catch(() => {
        // Root folder might already exist, ignore error
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Update breadcrumb when document data changes
  useEffect(() => {
    if (documentsData) {
      const breadcrumbDropStyle = (targetId) =>
        dropTargetId === targetId
          ? {
              background: "#e6f4ff",
              borderRadius: 4,
              padding: "0 4px",
              outline: "2px solid #1677ff",
            }
          : {};

      const makeDroppableProps = (targetId) =>
        isOwnerOrManager
          ? {
              onDragOver: (e) => {
                if (!draggingIds.length) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                setDropTargetId(targetId);
              },
              onDragLeave: () => {
                setDropTargetId((prev) => (prev === targetId ? null : prev));
              },
              onDrop: (e) => handleMoveDrop(targetId, e),
            }
          : {};

      const items = [
        {
          title: (
            <span
              {...makeDroppableProps("root")}
              style={breadcrumbDropStyle("root")}
            >
              <Button
                type="link"
                icon={<HomeOutlined />}
                style={{
                  padding: 0,
                  height: "auto",
                }}
                onClick={() => {
                  setDocumentId(null);
                  setBreadcrumbPath([]);
                  setPagination((prev) => ({
                    ...prev,
                    current: 1,
                  }));
                  setFilters({});
                  setSearchTexts({});
                  setSearchedColumns([]);
                }}
              >
                Home
              </Button>
            </span>
          ),
        },
      ];

      // Add all breadcrumb path items
      breadcrumbPath.forEach((item, index) => {
        items.push({
          title: (
            <span
              {...makeDroppableProps(item.id)}
              style={breadcrumbDropStyle(item.id)}
            >
              <Button
                type="link"
                style={{
                  padding: 0,
                  height: "auto",
                }}
                onClick={() => {
                  setDocumentId(item.id);
                  // Truncate path to this item
                  setBreadcrumbPath(breadcrumbPath.slice(0, index + 1));
                  setPagination((prev) => ({
                    ...prev,
                    current: 1,
                  }));
                  setFilters({});
                  setSearchTexts({});
                  setSearchedColumns([]);
                }}
              >
                {item.name}
              </Button>
            </span>
          ),
        });
      });

      // Add current folder if not in path
      if (
        documentId &&
        documentsData.name &&
        !breadcrumbPath.find((p) => p.id === documentId)
      ) {
        items.push({
          title: documentsData.name,
        });
      }

      setBreadcrumbItems(items);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    documentsData,
    documentId,
    breadcrumbPath,
    draggingIds,
    dropTargetId,
    isOwnerOrManager,
  ]);

  // Update pagination total when data changes; clamp page if now out of range
  // (e.g. moving/deleting the last item on page 2)
  useEffect(() => {
    if (documentsData && documentsData.total !== undefined) {
      setPagination((prev) => {
        const total = documentsData.total;
        const maxPage = Math.max(1, Math.ceil(total / prev.pageSize) || 1);
        const current = Math.min(prev.current, maxPage);
        if (prev.total === total && prev.current === current) {
          return prev;
        }
        return {
          ...prev,
          total,
          current,
        };
      });
    }
  }, [documentsData]);

  // Combine folders and files for table
  const tableData = documentsData
    ? [
        ...documentsData.folders.map((folder) => ({
          ...folder,
          key: folder.id,
          isFolder: true,
        })),
        ...documentsData.files.map((file) => ({
          ...file,
          key: file.id,
          isFolder: false,
        })),
      ]
    : [];

  // Create folder mutation
  const createFolderMutation = useMutation({
    mutationFn: (folderData) =>
      createDocument(projectId, { ...folderData, document_type: 1 }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: ["documents", projectId, documentId],
      });
      setIsCreateFolderModalVisible(false);
      // Reset pagination to first page if flag is set
      if (response?.reset_pagination) {
        setPagination((prev) => ({
          ...prev,
          current: 1,
        }));
      }
      notification.success({ message: "Folder created successfully" });
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.detail?.message ||
        error.response?.data?.detail ||
        error.message;
      notification.error({
        message: "Failed to create folder",
        description: errorMessage,
      });
    },
  });

  // Upload files mutation
  const uploadFilesMutation = useMutation({
    mutationFn: ({ folderId, files, uploadState }) =>
      uploadFolderTreeToDocument(projectId, folderId, files, uploadState),
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: ["documents", projectId, documentId],
      });
      setIsUploadFileModalVisible(false);
      // Reset pagination to first page if flag is set
      if (response?.reset_pagination) {
        setPagination((prev) => ({
          ...prev,
          current: 1,
        }));
      }
      notification.success({ message: "Files uploaded successfully" });
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.detail?.message ||
        error.response?.data?.detail ||
        error.message;
      notification.error({
        message: "Failed to upload files",
        description: errorMessage,
      });
    },
  });

  // Delete documents mutation
  const deleteDocumentsMutation = useMutation({
    mutationFn: (documents) => deleteDocuments(projectId, documents),
    onSuccess: () => {
      setSelectedRowKeys([]);
      notification.success({ message: "Documents deleted successfully" });
      return queryClient.invalidateQueries({
        queryKey: ["documents", projectId, documentId],
      });
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.detail?.message ||
        error.response?.data?.detail ||
        error.message;
      notification.error({
        message: "Failed to delete documents",
        description: errorMessage,
      });
    },
  });

  // Update document mutation (rename/permissions)
  const updateDocumentMutation = useMutation({
    mutationFn: ({ documentId, data }) =>
      updateDocument(projectId, documentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["documents", projectId, documentId],
      });
      setIsUpdateModalVisible(false);
      setSelectedDocument(null);
      notification.success({ message: "Document updated successfully" });
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.detail?.message ||
        error.response?.data?.detail ||
        error.message;
      notification.error({
        message: "Failed to update document",
        description: errorMessage,
      });
    },
  });

  // Move documents mutation (drag & drop)
  const moveDocumentsMutation = useMutation({
    mutationFn: async ({ documentIds, targetParentId }) => {
      const results = [];
      for (const id of documentIds) {
        results.push(await moveDocument(projectId, id, targetParentId));
      }
      return results;
    },
    onSuccess: (_data, variables) => {
      setSelectedRowKeys([]);
      const count = variables.documentIds.length;
      notification.success({
        message:
          count === 1
            ? "Item moved successfully"
            : `${count} items moved successfully`,
      });
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.detail?.message ||
        error.response?.data?.detail ||
        error.message;
      notification.error({
        message: "Failed to move",
        description: errorMessage,
      });
    },
    onSettled: () => {
      // Invalidate even on partial multi-move failure so UI doesn't show stale rows
      queryClient.invalidateQueries({ queryKey: ["documents", projectId] });
      setDraggingIds([]);
      setDropTargetId(null);
    },
  });

  const getIdsToMove = (draggedId) => {
    if (selectedRowKeys.includes(draggedId) && selectedRowKeys.length > 1) {
      return selectedRowKeys;
    }
    return [draggedId];
  };

  const canDropOntoTarget = (targetFolderId, idsBeingMoved) => {
    if (!targetFolderId || !idsBeingMoved?.length) return false;
    if (idsBeingMoved.includes(targetFolderId)) return false;
    // Don't move into the folder we're currently viewing (no-op)
    if (documentId && String(targetFolderId) === String(documentId)) {
      return false;
    }
    // At root, "root" is always valid unless already at root with same parent — handled by API
    return true;
  };

  const handleMoveDrop = (targetParentId, e) => {
    e.preventDefault();
    e.stopPropagation();
    const draggedId =
      e.dataTransfer.getData("text/document-id") || draggingIds[0];
    if (!draggedId) {
      setDropTargetId(null);
      return;
    }
    // Already viewing root — dropping on Home is a no-op
    if (targetParentId === "root" && !documentId) {
      setDropTargetId(null);
      return;
    }
    const idsToMove = getIdsToMove(draggedId).filter(
      (id) => String(id) !== String(targetParentId),
    );
    if (!idsToMove.length || !canDropOntoTarget(targetParentId, idsToMove)) {
      setDropTargetId(null);
      return;
    }
    // Don't drop into the folder currently being viewed
    if (documentId && String(targetParentId) === String(documentId)) {
      setDropTargetId(null);
      return;
    }
    moveDocumentsMutation.mutate({
      documentIds: idsToMove,
      targetParentId,
    });
  };

  const handleRowDragStart = (record, e) => {
    if (!isOwnerOrManager) {
      e.preventDefault();
      return;
    }
    suppressClickRef.current = true;
    const idsToMove = getIdsToMove(record.id);
    setDraggingIds(idsToMove);
    e.dataTransfer.setData("text/document-id", String(record.id));
    e.dataTransfer.setData("text/document-ids", JSON.stringify(idsToMove));
    e.dataTransfer.effectAllowed = "move";

    // Custom drag preview: file/folder name only (browser default would show full row)
    const ghost = document.createElement("div");
    ghost.className =
      "fixed -top-[1000px] -left-[1000px] flex items-center gap-2 max-w-[280px] px-3 py-2 bg-white border border-gray-200 rounded-md shadow-md text-sm text-gray-800 pointer-events-none";

    const accent = document.createElement("span");
    accent.className = `inline-block w-2 h-2 rounded-sm shrink-0 ${
      record.isFolder ? "bg-amber-400" : "bg-blue-500"
    }`;

    const label = document.createElement("span");
    label.className = "truncate";
    label.textContent =
      idsToMove.length > 1
        ? `Moving ${idsToMove.length} items`
        : record.name || "Item";

    ghost.appendChild(accent);
    ghost.appendChild(label);
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 12, 12);
    window.setTimeout(() => {
      if (ghost.parentNode) {
        document.body.removeChild(ghost);
      }
    }, 0);
  };

  const handleRowDragEnd = () => {
    setDraggingIds([]);
    setDropTargetId(null);
    window.setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
    savePreferredPageSize(newPagination.pageSize, "documents");
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    if (!searchedColumns.includes(dataIndex)) {
      setSearchedColumns([...searchedColumns, dataIndex]);
    }
    setSearchTexts((prev) => ({ ...prev, [dataIndex]: selectedKeys[0] }));
    setFilters((prev) => ({ ...prev, [dataIndex]: selectedKeys[0] }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleReset = (clearFilters, confirm, dataIndex) => {
    setSearchedColumns((prev) => prev.filter((col) => col !== dataIndex));
    clearFilters();
    confirm();
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[dataIndex];
      return newFilters;
    });
    setSearchTexts((prev) => {
      const updated = { ...prev };
      delete updated[dataIndex];
      return updated;
    });
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() =>
              clearFilters && handleReset(clearFilters, confirm, dataIndex)
            }
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1677ff" : undefined,
        }}
      />
    ),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current.select(), 100);
      }
    },
    render: (text, record) => {
      const isClickable =
        record.isFolder ||
        (record.file_url &&
          (record.file_type === "application/pdf" ||
            (record.file_type && record.file_type.startsWith("image/"))));

      const isSearched = searchedColumns.includes(dataIndex);
      const searchWords = [];
      if (searchedColumns.includes(dataIndex))
        searchWords.push(searchTexts[dataIndex]);

      const content = isSearched ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={searchWords}
          autoEscape
          textToHighlight={
            text !== undefined && text !== null ? text.toString() : ""
          }
        />
      ) : (
        text
      );

      return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Button
            type={isClickable ? "link" : "text"}
            icon={
              record.isFolder ? (
                <FolderFilled style={{ color: "#faad14" }} />
              ) : record.file_type === "application/pdf" ? (
                <FilePdfOutlined />
              ) : (
                <FileOutlined />
              )
            }
            onClick={isClickable ? () => handleFolderClick(record) : undefined}
            style={!isClickable ? { color: "inherit", cursor: "default" } : {}}
          >
            <Typography.Text
              strong={record.isFolder}
              style={record.isFolder ? { color: "#7a4a00" } : {}}
            >
              {content}
            </Typography.Text>
          </Button>
        </div>
      );
    },
  });

  const handleClearAllFilters = () => {
    setFilters({});
    setSearchTexts({});
    setSearchedColumns([]);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleFolderClick = (record) => {
    if (suppressClickRef.current) return;
    if (record.isFolder) {
      // Add to breadcrumb path
      setBreadcrumbPath([
        ...breadcrumbPath,
        { id: record.id, name: record.name },
      ]);
      setDocumentId(record.id);
      setSelectedRowKeys([]);
      // Reset to first page when navigating to a new folder
      setPagination((prev) => ({
        ...prev,
        current: 1,
      }));
      setFilters({});
      setSearchTexts({});
      setSearchedColumns([]);
    } else {
      // Only PDF and image files can be opened in browser
      if (record.file_type === "application/pdf" && record.file_url) {
        setPreviewDocument(record);
        setIsPreviewModalVisible(true);
      } else if (
        record.file_type &&
        record.file_type.startsWith("image/") &&
        record.file_url
      ) {
        window.open(record.file_url, "_blank", "noopener,noreferrer");
      }
      // Other file types (xlsx, docx, etc.) are not clickable
    }
  };

  const handleDelete = () => {
    const documentsToDelete = selectedRowKeys.map((key) => {
      const item = tableData.find((d) => d.key === key);
      return {
        id: key,
        document_type: item.isFolder ? 1 : 2,
      };
    });
    deleteDocumentsMutation.mutate(documentsToDelete);
  };

  const handleUpdate = (record) => {
    setSelectedDocument(record);
    setIsUpdateModalVisible(true);
  };

  const handleDeleteSingle = (record) => {
    const documentToDelete = {
      id: record.id,
      document_type: record.isFolder ? 1 : 2,
    };
    deleteDocumentsMutation.mutate([documentToDelete]);
  };

  const handleDownload = async (record) => {
    setDownloadingDocumentId(record.id);
    try {
      notification.info({
        message: "Preparing download",
        description: "Please wait while we prepare your file.",
      });
      const response = await downloadDocument(projectId, record.id);
      const filename = record.isFolder ? `${record.name}.zip` : record.name;
      downloadBlob(response.data, filename);
      notification.success({ message: "Download started" });
    } catch (error) {
      notification.error({
        message: "Failed to download",
        description: error.response?.data?.detail || error.message,
      });
    } finally {
      setDownloadingDocumentId(null);
    }
  };

  const runMergeAndDownload = async (mergeFilters) => {
    setIsMergingPDFs(true);
    try {
      notification.info({
        message: "Merging PDFs...",
        description: "System is merging PDFs, please wait...",
        duration: 3,
      });
      const response = await mergeDocumentFolderPDFs(
        projectId,
        documentId,
        mergeFilters,
      );
      const folderName = documentsData?.name || "documents";
      const cleanFolderName = folderName
        .replace(/[^a-z0-9]/gi, "_")
        .toLowerCase();
      const timestamp = new Date().toISOString().split("T")[0];
      downloadBlob(response.data, `${cleanFolderName}_merged_${timestamp}.pdf`);
      notification.success({
        message: "PDFs merged successfully",
        description: "The merged PDF has been downloaded.",
      });
    } catch (error) {
      let errorMessage =
        error.message || "An error occurred while merging PDFs";
      const data = error.response?.data;
      if (data instanceof Blob) {
        const parsed = await data
          .text()
          .then(JSON.parse)
          .catch(() => null);
        errorMessage =
          parsed?.detail?.message || parsed?.detail || errorMessage;
      } else if (data?.detail) {
        errorMessage = data.detail.message || data.detail;
      }
      notification.error({
        message: "Failed to merge PDFs",
        description: errorMessage,
      });
    } finally {
      setIsMergingPDFs(false);
    }
  };

  const handleMergeAndDownloadPDFs = async () => {
    if (!documentId) return;
    const mergeFilters = filters;
    setIsMergingPDFs(true);
    let summary;
    try {
      summary = await fetchDocumentMergeSummary(
        projectId,
        documentId,
        mergeFilters,
      );
    } catch (error) {
      notification.error({
        message: "Failed to merge PDFs",
        description:
          error.response?.data?.detail?.message ||
          error.response?.data?.detail ||
          error.message,
      });
      return;
    } finally {
      setIsMergingPDFs(false);
    }

    const toMB = (bytes) =>
      `${Math.max(1, Math.round(bytes / (1024 * 1024)))} MB`;

    if (!summary.pdf_count) {
      notification.warning({
        message: "No PDF files to merge",
        description: mergeFilters.name
          ? "Only PDF files can be merged. No PDF files match the current Name filter."
          : "Only PDF files can be merged. This folder has no PDF files.",
      });
      return;
    }

    if (summary.total_pdf_bytes > summary.max_bytes) {
      Modal.warning({
        title: "Folder is too large to merge",
        content: `The PDFs in this folder are ${toMB(summary.total_pdf_bytes)}. The merge limit is ${toMB(summary.max_bytes)}. Use the Name filter to merge fewer files.`,
      });
      return;
    }

    Modal.confirm({
      title: "Merge & Download",
      content: (
        <div>
          <p>
            Only PDF files are merged. {summary.pdf_count} PDF file
            {summary.pdf_count !== 1 ? "s" : ""} (
            {toMB(summary.total_pdf_bytes)}) in this folder will be merged into
            one PDF. Subfolders are not included.
          </p>
          {mergeFilters.name && (
            <p>
              Only items matching the Name filter "{mergeFilters.name}" are
              included.
            </p>
          )}
          {summary.skipped_count > 0 && (
            <p>
              {summary.skipped_count} other file
              {summary.skipped_count !== 1 ? "s" : ""} (Excel, Word, images,
              etc.) will not be included.
            </p>
          )}
        </div>
      ),
      okText: "Merge & Download",
      cancelText: "Cancel",
      onOk: () => {
        runMergeAndDownload(mergeFilters);
      },
    });
  };

  const handlePreview = (record) => {
    if (record.isFolder) return;

    if (record.file_type === "application/pdf" && record.file_url) {
      // PDF files: show preview modal
      setPreviewDocument(record);
      setIsPreviewModalVisible(true);
    } else if (record.file_url) {
      // Other file types: open S3 link in new tab
      window.open(record.file_url, "_blank", "noopener,noreferrer");
    }
  };

  const handleNotifyUsers = (record) => {
    setDocumentToNotify(record);
    setIsNotifyUsersModalVisible(true);
  };

  const handleSendNotification = async (userIds) => {
    if (!documentToNotify) return;

    setNotifyingDocumentId(documentToNotify.id);
    setIsNotifyUsersModalVisible(false);

    try {
      const result = await notifyProjectUsersAboutDocument(
        projectId,
        documentToNotify.id,
        userIds,
      );
      notification.success({
        message: "Notification sent successfully",
        description: `Email sent to ${result.recipients_count} project user${result.recipients_count !== 1 ? "s" : ""}`,
      });
    } catch (error) {
      let errorMessage = "An unknown error occurred";

      if (error.response?.data) {
        if (typeof error.response.data === "string") {
          try {
            const errorData = JSON.parse(error.response.data);
            errorMessage =
              errorData?.detail?.message || errorData?.detail || errorMessage;
          } catch {
            errorMessage = error.response.data;
          }
        } else {
          errorMessage =
            error.response.data?.detail?.message ||
            error.response.data?.detail ||
            errorMessage;
        }
      }

      notification.error({
        message: "Failed to send notification",
        description: errorMessage,
      });
    } finally {
      setNotifyingDocumentId(null);
      setDocumentToNotify(null);
    }
  };

  const menuItems = [
    {
      key: "create-folder",
      label: "Create Folder",
      icon: <PlusOutlined />,
      onClick: () => setIsCreateFolderModalVisible(true),
    },
    {
      key: "upload-file",
      label: "Upload File",
      icon: <UploadOutlined />,
      onClick: () => setIsUploadFileModalVisible(true),
    },
  ];

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      ...getColumnSearchProps("name"),
    },
    {
      title: "Updated At",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (date) => (date ? formatDateLocal(date) : ""),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        return (
          <Space>
            {isOwnerOrManager && (
              <Button
                icon={<EditOutlined />}
                size="medium"
                onClick={() => handleUpdate(record)}
                style={{ borderRadius: "4px" }}
              />
            )}
            <Button
              icon={<DownloadOutlined />}
              size="medium"
              onClick={() => handleDownload(record)}
              loading={downloadingDocumentId === record.id}
              disabled={downloadingDocumentId === record.id}
              style={{ borderRadius: "4px" }}
            />
            {!record.isFolder &&
              record.file_url &&
              (record.file_type === "application/pdf" ||
                (record.file_type &&
                  record.file_type.startsWith("image/"))) && (
                <Button
                  icon={<EyeOutlined />}
                  size="medium"
                  onClick={() => handlePreview(record)}
                  style={{ borderRadius: "4px" }}
                />
              )}
            <Button
              icon={<MailOutlined />}
              size="medium"
              onClick={() => handleNotifyUsers(record)}
              loading={notifyingDocumentId === record.id}
              disabled={notifyingDocumentId === record.id}
              title="Notify project users"
              style={{ borderRadius: "4px" }}
            />
            {isOwnerOrManager && record.isFolder && (
              <Button
                icon={<ShareAltOutlined />}
                size="medium"
                onClick={() => setFolderToShare(record)}
                title="Share public link"
                style={{ borderRadius: "4px" }}
              />
            )}
            {isOwnerOrManager && (
              <Popconfirm
                title="Are you sure you want to delete this document?"
                description={
                  record.isFolder
                    ? `This will delete "${record.name}" and ALL folders and files inside it. This action cannot be undone.`
                    : `This will delete "${record.name}". This action cannot be undone.`
                }
                onConfirm={() => handleDeleteSingle(record)}
                okText="Yes"
                cancelText="No"
                okButtonProps={{ danger: true }}
              >
                <Button
                  icon={<DeleteOutlined />}
                  size="medium"
                  danger
                  style={{ borderRadius: "4px" }}
                />
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  if (!projectId) {
    return <div>Please select a project</div>;
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Documents</h1>
      <div className="flex gap-4 mb-4 items-center">
        <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
          <Button type="primary" icon={<PlusOutlined />}>
            New
          </Button>
        </Dropdown>
        <Breadcrumb items={breadcrumbItems} />
        {documentId && (
          <Button
            icon={<MergeCellsOutlined />}
            onClick={handleMergeAndDownloadPDFs}
            disabled={!tableData || tableData.length === 0 || isMergingPDFs}
            loading={isMergingPDFs}
            style={{ marginLeft: 16 }}
          >
            Merge & Download
          </Button>
        )}
        {Object.keys(filters).length > 0 && (
          <Button
            icon={<ClearOutlined />}
            onClick={handleClearAllFilters}
            style={{ marginLeft: 16 }}
          >
            Clear All Filters
          </Button>
        )}
        <div className="flex gap-2 ml-auto">
          {isOwnerOrManager && selectedRowKeys.length > 0 && (
            <Popconfirm
              title="Delete selected documents?"
              description="This action cannot be undone."
              onConfirm={handleDelete}
              okText="Yes"
              cancelText="No"
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={deleteDocumentsMutation.isPending}
              >
                Delete Selected
              </Button>
            </Popconfirm>
          )}
        </div>
      </div>

      <Spin
        spinning={
          documentsLoading ||
          moveDocumentsMutation.isPending ||
          deleteDocumentsMutation.isPending
        }
      >
        <Table
          columns={columns}
          dataSource={tableData}
          rowSelection={rowSelection}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          onChange={handleTableChange}
          loading={
            documentsLoading ||
            moveDocumentsMutation.isPending ||
            deleteDocumentsMutation.isPending
          }
          onRow={(record) => {
            const isDragging = draggingIds.includes(record.id);
            const isDropTarget = record.isFolder && dropTargetId === record.id;
            const canAcceptDrop =
              isOwnerOrManager &&
              record.isFolder &&
              draggingIds.length > 0 &&
              !draggingIds.includes(record.id);

            return {
              draggable: isOwnerOrManager,
              onDragStart: (e) => handleRowDragStart(record, e),
              onDragEnd: handleRowDragEnd,
              onDragOver: (e) => {
                if (!canAcceptDrop) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                if (dropTargetId !== record.id) {
                  setDropTargetId(record.id);
                }
              },
              onDragLeave: (e) => {
                // Only clear when leaving the row itself
                if (e.currentTarget.contains(e.relatedTarget)) {
                  return;
                }
                setDropTargetId((prev) => (prev === record.id ? null : prev));
              },
              onDrop: (e) => {
                if (!record.isFolder) return;
                handleMoveDrop(record.id, e);
              },
              style: {
                cursor: isOwnerOrManager ? "grab" : undefined,
                opacity: isDragging ? 0.45 : 1,
                background: isDropTarget ? "#e6f4ff" : undefined,
                outline: isDropTarget ? "2px solid #1677ff" : undefined,
                outlineOffset: isDropTarget ? -2 : undefined,
                transition: "background 0.15s ease, opacity 0.15s ease",
              },
            };
          }}
        />
      </Spin>

      <CreateDocumentModal
        visible={isCreateFolderModalVisible}
        onCancel={() => setIsCreateFolderModalVisible(false)}
        onOk={async (values) => {
          // If no documentId, get root folder or create it
          let targetFolderId = documentId;
          if (!targetFolderId) {
            try {
              const rootDoc = await fetchDocuments(projectId, null);
              if (rootDoc && rootDoc.id) {
                targetFolderId = rootDoc.id;
              } else {
                // Create root folder if it doesn't exist
                const rootResult = await createRootDocument(projectId);
                targetFolderId = rootResult.id;
              }
            } catch (error) {
              // Root folder might already exist, use null as parent_id
              targetFolderId = null;
            }
          }
          createFolderMutation.mutate({
            ...values,
            parent_id: targetFolderId,
          });
        }}
        loading={createFolderMutation.isPending}
      />

      <UploadFileModal
        visible={isUploadFileModalVisible}
        allowFolders
        onCancel={() => setIsUploadFileModalVisible(false)}
        onOk={async (files, uploadState) => {
          // If no documentId, get root folder
          let targetFolderId = documentId;
          if (!targetFolderId) {
            try {
              const rootDoc = await fetchDocuments(projectId, null);
              if (rootDoc && rootDoc.id) {
                targetFolderId = rootDoc.id;
              } else {
                // Create root folder if it doesn't exist
                const rootResult = await createRootDocument(projectId);
                targetFolderId = rootResult.id || "root";
              }
            } catch (error) {
              // Use "root" as fallback, backend will handle it
              targetFolderId = "root";
            }
          }
          uploadFilesMutation.mutate({
            folderId: targetFolderId,
            files,
            uploadState,
          });
        }}
        loading={uploadFilesMutation.isPending}
      />

      <UpdateDocumentModal
        visible={isUpdateModalVisible}
        document={selectedDocument}
        onCancel={() => {
          setIsUpdateModalVisible(false);
          setSelectedDocument(null);
        }}
        onOk={(values) => {
          if (selectedDocument) {
            updateDocumentMutation.mutate({
              documentId: selectedDocument.id,
              data: values,
            });
          }
        }}
        loading={updateDocumentMutation.isPending}
      />

      <DocumentPreview
        document={previewDocument}
        visible={isPreviewModalVisible}
        onClose={() => {
          setIsPreviewModalVisible(false);
          setPreviewDocument(null);
        }}
      />

      <NotifyUsersModal
        visible={isNotifyUsersModalVisible}
        onClose={() => {
          setIsNotifyUsersModalVisible(false);
          setDocumentToNotify(null);
        }}
        folder={documentToNotify}
        onNotify={handleSendNotification}
      />

      <ShareFolderModal
        visible={!!folderToShare}
        onClose={() => setFolderToShare(null)}
        folderName={folderToShare?.name}
        loadShare={() => fetchDocumentFolderShare(projectId, folderToShare.id)}
        enableShare={() => shareDocumentFolder(projectId, folderToShare.id)}
        disableShare={() => unshareDocumentFolder(projectId, folderToShare.id)}
      />
    </div>
  );
};

export default Documents;
